import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Fade,
  Paper,
  Divider,
  Stack,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Description as DocumentIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Add as AddIcon,
  OpenInNew as OpenIcon,
  Label as LabelIcon
} from '@mui/icons-material';
import Header from './Header';

const DocumentTemplateList = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const url = process.env.REACT_APP_SERVER_URL;

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/templates`);
      setTemplates(response.data.templates);
      setError('');
    } catch (err) {
      setError('Failed to load templates');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDocument = (templateId) => {
    window.open(`${url}/templates/${templateId}`, '_blank');
    //once the window is opened, we will autofill the fields by tenancyId
    //${url}/tenancy/${tenancyId}/templates/${templateId} - this will fill the fields by that tenancyId
    
  };

  const handleUploadNew = () => {
    navigate('/template/upload');
  };

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
          <Stack spacing={2} alignItems="center">
            <CircularProgress size={60} />
            <Typography variant="h6" color="text.secondary">
              Loading templates...
            </Typography>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <>
    <Header/>
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      py: 4
    }}>
      <Container maxWidth="lg">
        
        {/* Header Section */}
        <Fade in timeout={800}>
          <Paper 
            elevation={12} 
            sx={{ 
              p: 4, 
              mb: 4, 
              borderRadius: 3,
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
            }}
          >
            <Stack 
              direction="row" 
              justifyContent="space-between" 
              alignItems="center"
              flexWrap="wrap"
              gap={2}
            >
              <Box>
                <Typography 
                  variant="h3" 
                  fontWeight={700}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 1
                  }}
                >
                  Document Templates
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Manage and view your document templates
                </Typography>
              </Box>
              
              <Button
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                onClick={handleUploadNew}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  px: 4,
                  py: 1.5,
                  fontSize: 16,
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none',
                  boxShadow: '0 8px 16px rgba(102, 126, 234, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                    boxShadow: '0 12px 24px rgba(102, 126, 234, 0.4)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Upload New Template
              </Button>
            </Stack>

            {templates.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Divider />
                <Stack direction="row" spacing={3} sx={{ mt: 2 }}>
                  <Chip 
                    icon={<DocumentIcon />}
                    label={`${templates.length} Template${templates.length !== 1 ? 's' : ''}`}
                    color="primary"
                    variant="outlined"
                  />
                  <Chip 
                    icon={<LabelIcon />}
                    label={`${templates.reduce((sum, t) => sum + t.fieldsCount, 0)} Total Fields`}
                    color="secondary"
                    variant="outlined"
                  />
                </Stack>
              </Box>
            )}
          </Paper>
        </Fade>

        {/* Error Alert */}
        {error && (
          <Fade in>
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          </Fade>
        )}

        {/* Empty State */}
        {templates.length === 0 && !error && (
          <Fade in timeout={1000}>
            <Paper 
              elevation={6}
              sx={{ 
                p: 8, 
                textAlign: 'center',
                borderRadius: 3,
                background: 'rgba(255, 255, 255, 0.95)'
              }}
            >
              <DocumentIcon sx={{ fontSize: 80, color: '#9ca3af', mb: 2 }} />
              <Typography variant="h5" fontWeight={600} color="text.secondary" gutterBottom>
                No Templates Yet
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Get started by uploading your first document template
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                onClick={handleUploadNew}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Upload Your First Template
              </Button>
            </Paper>
          </Fade>
        )}

        {/* Templates Grid */}
        {templates.length > 0 && (
          <Grid container spacing={3}>
            {templates.map((template, index) => (
              <Grid item xs={12} sm={6} md={4} key={template.id}>
                <Fade in timeout={800 + (index * 100)}>
                  <Card 
                    elevation={8}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                      }
                    }}
                  >
                    {/* Card Header with Icon */}
                    <Box sx={{ 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      p: 3,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2
                    }}>
                      <Box sx={{
                        background: 'rgba(255, 255, 255, 0.2)',
                        borderRadius: 2,
                        p: 1.5,
                        display: 'flex'
                      }}>
                        <DocumentIcon sx={{ fontSize: 32, color: 'white' }} />
                      </Box>
                      <Typography 
                        variant="h6" 
                        fontWeight={700}
                        sx={{ 
                          color: 'white',
                          flex: 1,
                          wordBreak: 'break-word'
                        }}
                      >
                        {template.name}
                      </Typography>
                    </Box>

                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Stack spacing={2}>
                        {/* Fields Count */}
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          gap: 1,
                          p: 2,
                          background: '#f0f9ff',
                          borderRadius: 2,
                          border: '1px solid #bae6fd'
                        }}>
                          <LabelIcon sx={{ color: '#0284c7' }} />
                          <Typography variant="body1" fontWeight={600} color="#0284c7">
                            {template.fieldsCount} Field{template.fieldsCount !== 1 ? 's' : ''}
                          </Typography>
                        </Box>

                        {/* Fields Preview */}
                        {template.fields && template.fields.length > 0 && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                              Fields:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {template.fields.slice(0, 3).map((field) => (
                                <Chip 
                                  key={field}
                                  label={field}
                                  size="small"
                                  sx={{ 
                                    fontSize: 11,
                                    height: 24
                                  }}
                                />
                              ))}
                              {template.fields.length > 3 && (
                                <Chip 
                                  label={`+${template.fields.length - 3} more`}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ fontSize: 11, height: 24 }}
                                />
                              )}
                            </Box>
                          </Box>
                        )}
                      </Stack>
                    </CardContent>

                    <Divider />

                    <CardActions sx={{ p: 2, gap: 1 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<OpenIcon />}
                        onClick={() => handleOpenDocument(template.id)}
                        sx={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          fontWeight: 600,
                          textTransform: 'none',
                          py: 1,
                          '&:hover': {
                            background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                          }
                        }}
                      >
                        Open Document
                      </Button>
                      
                      <Tooltip title="Edit Template" arrow>
                        <IconButton
                          sx={{
                            border: '1px solid #e0e0e0',
                            '&:hover': {
                              background: '#f5f5f5',
                              borderColor: '#667eea'
                            }
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </CardActions>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        )}

      </Container>
    </Box>
    </>
  );
};

export default DocumentTemplateList;